import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { MessageSquareText, X, Send, Sparkles, Loader2, RotateCcw } from "lucide-react";
import { modelLabel as labelFor } from "@/lib/models.mjs";
import { streamChat } from "@/lib/api";

import useAiSharing from "./useAiSharing";

const SUGGESTIONS = [
  "What should I fix first?",
  "Which pages rank but get no clicks?",
  "Where am I getting traffic but no conversions?",
  "Why might my traffic be changing?",
];

const MOBILE_QUERY = "(max-width: 639px)";
const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Chat.
 *
 * Two layouts from one component:
 *
 * Desktop, 640px and up. A 440px panel anchored bottom-right, at least 560px
 * tall and never taller than the viewport less 120px. Solid white body: the
 * previous `bg-white/85 backdrop-blur-xl` let dashboard numbers show through
 * the conversation, which is a poor trade for a glass effect.
 *
 * Mobile, below 640px. A full-height bottom sheet using `100dvh`, so the
 * browser chrome collapsing does not crop the input. Background scroll is
 * locked, focus is trapped and returned to the launcher on close, and the input
 * sits at 16px because anything smaller makes iOS Safari zoom the page on
 * focus, leaving the send button off screen.
 */
export default function ChatPanel({ propertyId, propertyName, range = 28, model = "claude", isDemo = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  const inputRef = useRef(null);
  const reduceMotion = useReducedMotion();

  const modelLabel = labelFor(model);
  const sharing = useAiSharing(propertyId, propertyName, model, isDemo);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(MOBILE_QUERY);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Reset the conversation when the property or model changes: the answers are
  // grounded in one site's numbers, so carrying them across would be misleading.
  useEffect(() => {
    setMessages([]);
    setFailed(null);
    sessionRef.current = null;
  }, [propertyId, model]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: reduceMotion ? "auto" : "smooth" });
  }, [messages, open, reduceMotion]);

  // Lock the background while the mobile sheet is open, otherwise the page
  // scrolls underneath the conversation.
  useEffect(() => {
    if (!open || !isMobile || typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [open, isMobile]);

  const close = useCallback(() => setOpen(false), []);

  // Focus goes back to the launcher after it re-mounts. Calling `.focus()`
  // inside `close()` is too early: the launcher only renders once `open` is
  // false, so at that point the node does not exist and focus falls to <body>.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) launcherRef.current?.focus();
    wasOpen.current = open;
  }, [open]);

  // Escape closes. Tab cycles inside the panel rather than escaping to the
  // dashboard behind it.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); close(); return; }
      if (e.key !== "Tab") return;
      const nodes = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes?.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    // Move focus into the panel so a keyboard user is not left behind it.
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => { document.removeEventListener("keydown", onKeyDown); clearTimeout(t); };
  }, [open, close]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setBusy(true);
    if (!await sharing.request()) { setBusy(false); return; }
    setInput("");
    setFailed(null);
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      await streamChat(
        propertyId, q, sessionRef.current, range, model,
        (delta) => setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
          return copy;
        }),
        (sid) => { sessionRef.current = sid; },
        () => setBusy(false),
      );
    } catch (e) {
      setMessages((m) => m.slice(0, -1));
      setFailed(q);
      setBusy(false);
    }
  };

  const panelClasses = isMobile
    ? "fixed inset-x-0 bottom-0 top-0 z-50 flex h-[100dvh] w-full flex-col overflow-hidden bg-white"
    : "fixed bottom-24 right-5 z-50 flex max-h-[calc(100dvh-120px)] min-h-[560px] w-[calc(100vw-2.5rem)] max-w-[440px] flex-col overflow-hidden rounded-panel border border-line bg-white shadow-overlay";

  return (
    <>
      {sharing.dialog}
      {/* The launcher hides while the panel is open, so it cannot sit on top of it. */}
      {!open && (
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chat-toggle-button"
          aria-expanded={false}
          className="fixed bottom-5 right-5 z-50 inline-flex min-h-touch items-center gap-2.5 rounded-full bg-navy-950 px-5 text-white shadow-overlay transition-colors duration-control ease-editorial hover:bg-navy-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-offwhite"
        >
          <MessageSquareText size={20} aria-hidden="true" />
          <span className="text-small font-semibold">Ask SEO Signals</span>
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal={isMobile || undefined}
            aria-label={`Ask SEO Signals about ${propertyName}`}
            initial={reduceMotion ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 18 }}
            transition={{ duration: 0.27, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className={panelClasses}
          >
            {/* header */}
            <div className="flex items-center gap-3 bg-navy-950 px-4 py-3.5 text-white">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-compact bg-white/10">
                <Sparkles size={17} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1 leading-tight">
                <div className="truncate font-heading text-small font-semibold">Ask about {propertyName}</div>
                <div className="truncate text-caption text-slate-300">
                  {isDemo ? "Sample walkthrough · no live AI" : `${modelLabel} · grounded in your Search Console and GA4 numbers`}
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                data-testid="chat-close-button"
                className="tap-safe inline-flex min-w-[44px] items-center justify-center gap-1.5 rounded-btn px-2 text-small font-semibold text-white transition-colors duration-control hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white lg:min-w-0 lg:px-3"
              >
                <X size={18} aria-hidden="true" /> <span className="sr-only sm:not-sr-only">Close</span>
              </button>
            </div>

            {/* messages */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
              {messages.length === 0 && (
                <div className="pt-2">
                  <p className="text-small text-slate-600">
                    {isDemo ? "Explore a fixed explanation of the sample data. This does not answer arbitrary questions with live AI." : <>Ask anything about {propertyName}. I answer using your real Search Console
                    and Analytics numbers — no generic advice.</>}
                  </p>
                  <div className="mt-4 space-y-2">
                    {(isDemo ? ["Show the sample walkthrough"] : SUGGESTIONS).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => send(s)}
                        data-testid={`chat-suggestion-${s.slice(0, 8)}`}
                        className="tap-safe flex w-full items-center rounded-compact border border-line bg-white px-3 text-left text-small text-navy-800 transition-colors duration-control ease-editorial hover:border-emerald-600 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-card px-3.5 py-2.5 text-small ${
                      m.role === "user"
                        // emerald-700, not emerald-600: white on 600 measures
                        // 3.77:1 and fails AA at this text size.
                        ? "rounded-br-sm bg-emerald-700 text-white"
                        : "rounded-bl-sm border border-line bg-white text-navy-950"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? (
                      <span className="inline-flex items-center gap-2 text-slate-600"><Loader2 size={14} className="animate-spin" aria-hidden="true" /> thinking…</span>
                    ) : "")}
                    {busy && m.role === "assistant" && i === messages.length - 1 && m.content && (
                      <span className="sp-cursor" aria-hidden="true" />
                    )}
                  </div>
                </div>
              ))}
              {failed && (
                <div data-testid="chat-error" className="rounded-compact border border-line bg-offwhite px-3.5 py-3">
                  <p className="text-small text-slate-600">
                    We couldn't reach the AI service. Your question is still here.
                  </p>
                  <button
                    type="button"
                    onClick={() => send(failed)}
                    data-testid="chat-retry-button"
                    className="mt-2 inline-flex items-center gap-1.5 rounded-btn border border-line-strong bg-white px-3 py-2 text-small font-semibold text-navy-950 transition-colors duration-control hover:bg-offwhite focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
                  >
                    <RotateCcw size={14} aria-hidden="true" /> Ask again
                  </button>
                </div>
              )}
            </div>

            {/* input. `env(safe-area-inset-bottom)` keeps the field clear of the
                home indicator on iOS when the sheet runs full height. */}
            <div
              className="border-t border-line bg-white p-3"
              style={{ paddingBottom: isMobile ? "calc(0.75rem + env(safe-area-inset-bottom))" : undefined }}
            >
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="flex items-center gap-2 rounded-compact border border-line-strong bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-emerald-600"
              >
                <label htmlFor="chat-input" className="sr-only">Ask about your site</label>
                <input
                  id="chat-input"
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={isDemo ? "Explore the sample walkthrough…" : "Ask about your site…"}
                  data-testid="chat-input"
                  // 16px. Below that, iOS Safari zooms on focus and the send
                  // button ends up off screen.
                  className="tap-safe flex-1 bg-transparent text-base text-navy-950 placeholder:text-slate-500 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={busy || !input.trim()}
                  data-testid="chat-send-button"
                  aria-label="Send"
                  className="tap-safe grid w-11 shrink-0 place-items-center rounded-btn bg-emerald-700 lg:w-9 text-white transition-colors duration-control hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:opacity-40"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
